/**
 * Pinned from `/tmp/integrate` (branch `integrate/wave-a`, read-only source):
 *   - src/modules/employees/employees.controller.ts + .service.ts + dto/*.ts
 *   - src/modules/attendance/attendance.controller.ts + .service.ts
 *     + attendance-policy.service.ts + dto/attendance.dto.ts
 *   - prisma/schema.prisma (Employee, EmployeeCompensation, TimeEntry,
 *     TimePunch, AttendanceException, AttendancePolicy)
 * One file, so a later drift on that side is a one-file fix here.
 *
 * ADR-0008 in three sentences that shape this module: an Employee is a
 * profile BESIDE a User, never the same row — a kitchen porter is rostered
 * and paid without ever widening the PIN space. Compensation lives in its
 * own table, reachable only through `compensation.view`/`.manage`, and
 * `EmployeesService` has no code path that can return it — this screen must
 * not be the code path that finally does. A punch is evidence: a correction
 * writes a NEW row and the database refuses to let the original be edited.
 */
import {
  AttendanceExceptionType,
  EmployeeDocumentType,
  EmploymentStatus,
  EmploymentType,
  GeofenceEnforcement,
  LocationCaptureMode,
  PayType,
  TimeEntryStatus,
  TimePunchSource,
  TimePunchType,
} from '@/lib/shared-types';

export {
  AttendanceExceptionType,
  EmployeeDocumentType,
  EmploymentStatus,
  EmploymentType,
  GeofenceEnforcement,
  LocationCaptureMode,
  PayType,
  TimeEntryStatus,
  TimePunchSource,
  TimePunchType,
};

export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(' · ');
  return (typeof message === 'string' && message.trim()) || fallback;
}

export function apiStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

// -------------------------------------------------------------- employees

export interface BranchAssignment {
  id: string;
  branchId: string;
  isPrimary: boolean;
}

/** `EMPLOYEE_SELECT` in `employees.service.ts` — the allow-list. Compensation is never in this shape. */
export interface Employee {
  id: string;
  tenantId: string;
  userId: string | null;
  employeeCode: string;
  firstName: string;
  lastName: string | null;
  jobTitle: string | null;
  status: EmploymentStatus;
  employmentType: EmploymentType;
  hireDate: string | null;
  terminationDate: string | null;
  terminationReason: string | null;
  email: string | null;
  phone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelation: string | null;
  preferredLanguage: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  branchAssignments: BranchAssignment[];
}

export interface EmployeeDocument {
  id: string;
  employeeId: string;
  type: EmployeeDocumentType;
  name: string;
  fileKey: string | null;
  reference: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeSkill {
  id: string;
  employeeId: string;
  name: string;
  level: number | null;
  certifiedAt: string | null;
  expiresAt: string | null;
}

/** `GET /employees/:id` — the profile, still with no compensation on it. */
export interface EmployeeProfile extends Employee {
  documents: EmployeeDocument[];
  skills: EmployeeSkill[];
}

export interface CreateEmployeeInput {
  employeeCode: string;
  firstName: string;
  lastName?: string;
  userId?: string;
  jobTitle?: string;
  status?: EmploymentStatus;
  employmentType?: EmploymentType;
  hireDate?: string;
  email?: string;
  phone?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
  preferredLanguage?: string;
  notes?: string;
  branches?: Array<{ branchId: string; isPrimary?: boolean }>;
}

export type UpdateEmployeeInput = Partial<
  Omit<CreateEmployeeInput, 'employeeCode' | 'userId' | 'branches'>
>;

export interface TerminateEmployeeInput {
  terminationDate: string;
  reason: string;
}

export interface UpsertDocumentInput {
  type: EmployeeDocumentType;
  name: string;
  fileKey?: string;
  reference?: string;
  issuedAt?: string;
  expiresAt?: string;
  notes?: string;
}

export interface UpsertSkillInput {
  name: string;
  level?: number;
  certifiedAt?: string;
  expiresAt?: string;
}

// ------------------------------------------------------------ compensation

/** `GET /employees/:id/compensation` — `compensation.view` ONLY. Never returned by any `employees.*`-gated route. */
export interface EmployeeCompensation {
  id: string;
  employeeId: string;
  payType: PayType;
  /** Decimal string. Never `Number(...)`'d for display — see `useCurrency`. */
  baseRate: string;
  currencyId: string;
  currency: { code: string; decimals: number; symbol: string };
  overtimeMultiplier: string;
  weeklyHours: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
  createdById: string | null;
  createdAt: string;
}

export interface CreateCompensationInput {
  payType: PayType;
  baseRate: string;
  currencyCode: string;
  overtimeMultiplier?: string;
  weeklyHours?: string;
  effectiveFrom: string;
  notes?: string;
}

// ------------------------------------------------------------- attendance

export interface TimePunch {
  id: string;
  employeeId: string;
  timeEntryId: string | null;
  branchId: string;
  type: TimePunchType;
  source: TimePunchSource;
  occurredAt: string;
  recordedAt: string;
  deviceId: string | null;
  recordedById: string | null;
  supersedesId: string | null;
  supersededById: string | null;
  correctionReason: string | null;
  latitude: number | null;
  longitude: number | null;
  accuracyMeters: number | null;
  locationScrubbedAt: string | null;
  distanceMeters: number | null;
}

export interface AttendanceException {
  id: string;
  timeEntryId: string | null;
  scheduledShiftId: string | null;
  type: AttendanceExceptionType;
  magnitude: number | null;
  detail: string | null;
  resolvedById: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
  timeEntry?: TimeEntry | null;
}

/** `ENTRY_INCLUDE` in `attendance.service.ts`. */
export interface TimeEntry {
  id: string;
  employeeId: string;
  branchId: string;
  scheduledShiftId: string | null;
  status: TimeEntryStatus;
  clockInAt: string;
  clockOutAt: string | null;
  businessDate: string;
  timezone: string;
  workedMinutes: number;
  breakMinutes: number;
  lateMinutes: number;
  earlyDepartureMinutes: number;
  overtimeMinutes: number;
  autoClosed: boolean;
  approvedById: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  employee: { id: string; employeeCode: string; firstName: string; lastName: string | null };
  branch: { id: string; name: string; code: string; timezone: string };
  punches: TimePunch[];
  exceptions: AttendanceException[];
  scheduledShift: { id: string; startsAt: string; endsAt: string; roleId: string | null } | null;
}

/** `GET /attendance/me` — am I clocked in, since when, and am I on a break. */
export interface CurrentAttendance {
  employeeId: string;
  clockedIn: boolean;
  entry: TimeEntry | null;
  onBreak: boolean;
}

export interface ClockInInput {
  branchId: string;
  employeeId?: string;
  deviceId?: string;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  idempotencyKey?: string;
}

export interface ClockOutInput {
  employeeId?: string;
  deviceId?: string;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  idempotencyKey?: string;
}

export interface BreakPunchInput {
  employeeId?: string;
  deviceId?: string;
  idempotencyKey?: string;
}

/** `POST /attendance/punches/correct` — amends. The original punch is never rewritten. */
export interface CorrectPunchInput {
  punchId: string;
  occurredAt: string;
  reason: string;
}

/** `POST /attendance/punches/missing` — records a punch that was never taken, with a reason. */
export interface AddMissingPunchInput {
  employeeId: string;
  branchId: string;
  type: TimePunchType;
  occurredAt: string;
  reason: string;
  timeEntryId?: string;
}

export interface QueryTimeEntriesParams {
  page?: number;
  limit?: number;
  branchId?: string;
  employeeId?: string;
  status?: TimeEntryStatus;
  from?: string;
  to?: string;
}

export interface QueryExceptionsParams {
  page?: number;
  limit?: number;
  branchId?: string;
  type?: AttendanceExceptionType;
  includeResolved?: boolean;
  from?: string;
  to?: string;
}

// ----------------------------------------------------------------- policy

/** `GET /attendance/policy` (effective) and `GET /attendance/policy/defaults`. */
export interface AttendancePolicy {
  id?: string;
  branchId: string | null;
  lateGraceMinutes: number;
  earlyDepartureGraceMinutes: number;
  earlyClockInGraceMinutes: number;
  dailyOvertimeThresholdMinutes: number;
  weeklyOvertimeThresholdMinutes: number;
  roundingMinutes: number;
  autoBreakMinutes: number;
  maxBreakMinutes: number;
  autoCloseAfterMinutes: number;
  requireAuthorizedDevice: boolean;
  /** OFF unless the venue opted in. Surfaced honestly — never defaulted to ON in the UI either. */
  locationCaptureMode: LocationCaptureMode;
  geofenceEnforcement: GeofenceEnforcement;
  geofenceRadiusMeters: number;
  locationRetentionDays: number;
}

export type UpdateAttendancePolicyInput = Partial<AttendancePolicy>;
